import './FullscreenReference.css';

import { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { IonIcon } from '@ionic/react';
import {
  checkmarkCircleOutline,
  closeCircleOutline,
  refreshOutline,
} from 'ionicons/icons';
import { ColumnsWitdhType, Dataset, ReferenceControl } from '../../types/types';
import {
  DEFAULT_LAZY_STATE,
  GRID_DATA_LIMIT,
  HEADER_MIN_HEIGHT_PX,
} from '../../constants';
import Grid from '../Manager/Grid/Grid';
import { isSkeletonRow } from '../Manager/Grid/utils/isSkeletonRow';
import { useDataset } from '../../api/hooks/useDataset';
import { useFilter } from '../../api/hooks/useFilter';
import { getColumnsWidth } from '../../utils/getColumnsWidth';
import {
  getMultisortFromDataset,
  isEmptyValue,
  scrollGridToTop,
  updateActiveSortingFromLazyState,
} from '../../utils/managerUtils';
import { getSupportedDefaultMatchMode } from '../../utils/filters/getSupportedDefaultMatchMode';
import { getDefaultLazyStateFilters } from '../../utils/getDefaultLazyStateFilters';
import Button from '../Button/Button';
import ApiError from '../ApiError/ApiError';
import { useTranslate } from '../../hooks/context/useTranslate';
import SeptimLoadingSpinner from '../Manager/SeptimLoadingSpinner/SeptimLoadingSpinner';
import DatasetButtons from '../Manager/DatasetButtons/DatasetButtons';
import { useSelectedRowsBatchUpdate } from '../../hooks/useSelectedRowsBatchUpdate';
import { useSelectedRowsChangedCachedQueryGuard } from '../../hooks/useSelectedRowsChangedCachedQueryGuard';
import { useSelectedRowsContext } from '../../hooks/context/useSelectedRowsContext';
import { useDatasetContext } from '../../hooks/context/useDatasetContext';
import { useDatasetData } from '../../api/hooks/useDatasetData';
import { getReferenceDataParamsString } from './utils/getReferenceDataParamsString';
import { getDataKey } from '../../utils/getDataKey';
import { useUserState } from '../../hooks/context/useUserState';
import { apiRequest } from '../../api/utils/apiRequest';
import { getDatasetDataApiRequestData } from '../../api/utils/requestData/getDatasetDataApiRequestData';
import { resolveTemplate } from '../../utils/json/resolveTemplate';
import RequestDataProvider from '../../context/RequestDataContext';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import Loading from '../Loading/Loading';
import { buildSelectedIdSet } from './utils/buildSelectedIdSet';
import { buildFinalIds } from './utils/buildFinalIds';
import { buildReferenceValueWithInsertedId } from './utils/buildReferenceValueWithInsertedId';
import { getDataFromDatasetRequest } from '../../api/utils/getDataFromDatasetRequest';
import { isContentTooLargeError } from '../../api/utils/isContentTooLargeError';
import { useAutoSelectInsertedRow } from './hooks/useAutoSelectInsertedRow';
import { useReferenceFilters } from './hooks/useReferenceFilters';
import { getReferenceValueDelimiter } from '../../utils/getReferenceValueDelimiter';
import { useFormTokenChainContextOptional } from '../../hooks/context/useFormTokenChainContextOptional';
import { getSubsetDataApiRequestData } from '../../api/utils/requestData/getSubsetDataApiRequestData';
import { getReferenceItemsParamArray } from './utils/getReferenceItemsParamArray';
import { useDatasetVisibilityResolve } from '../../hooks/useDatasetVisibilityResolve';
import { resolveDatasetItemsVisibility } from '../../utils/dataset/resolveDatasetItemsVisibility';
import HeaderLabel from './components/HeaderLabel/HeaderLabel';
import { getDatasetButtonsWrapperInlineStyle } from '../../utils/getDatasetButtonsWrapperInlineStyle';
import { withSyntheticRowIds } from '../../utils/withSyntheticRowIds';
import { DatasetButtonsOverflowState } from '../Manager/DatasetButtons/types';

interface FullscreenReferenceProps {
  datasetId: string;
  closeFullscreenReference: () => void;
  control: ReferenceControl;
  value: string;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  label: string;
  handleOnSubmit: (value: string) => void;
  autoSelectInsertedId?: string;
  isFormInput?: boolean;
  breadcrumbsHeight?: number;
}

const FullscreenReference = ({
  datasetId,
  closeFullscreenReference,
  control,
  value,
  referenceDatasets,
  setReferenceDatasets,
  label,
  handleOnSubmit,
  autoSelectInsertedId,
  isFormInput,
  breadcrumbsHeight = 0,
}: FullscreenReferenceProps) => {
  const valueDelimiter = getReferenceValueDelimiter(
    isFormInput,
    control.multiSelect
  );
  const {
    selectedReferenceRows: selectedRows,
    selectedReferenceVirtualRows: selectedVirtualRows,
    selectAllReferenceChecked: selectAllChecked,
    setSelectedReferenceRows: setSelectedRows,
    setSelectedReferenceVirtualRows: setSelectedVirtualRows,
    setSelectAllReferenceChecked: setSelectAllChecked,
    deselectedReferenceRowIds: deselectedRowIds,
    processSelectedReferenceRowsChange,
    processSelectAllReferenceChange,
    resetReferenceSelectedRows,
  } = useSelectedRowsContext();
  // Loading flag for submitFilter's "select-all → fetch all rows" path.
  // Disables the confirm button while the unpaginated fetch runs.
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [lazyState, setLazyState] = useState<any>(DEFAULT_LAZY_STATE);
  const [activeSorting, setActiveSorting] = useState<any[]>([]);
  const [disabledLazyLoading, setDisabledLazyLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [offset, setOffset] = useState(0);
  const [lazyLoading, setLazyLoading] = useState(false);
  const [cachedQueryId, setCachedQueryId] = useState('');
  const [columnsWidth, setColumnsWidth] = useState<ColumnsWitdhType>(undefined);
  const [filter, setFilter] = useState(null);
  const [gridLoading, setGridLoading] = useState(true);
  const [datasetLoading, setDatasetLoading] = useState(true);
  // Floor for the dataset row so the confirm/cancel buttons are never clipped.
  const [datasetMinWidth, setDatasetMinWidth] = useState<number>(0);

  const {
    data: fetchedDataset,
    fetch: fetchDataset,
    error: datasetError,
  } = useDataset(datasetId);
  const {
    state: { token: sessionToken },
  } = useUserState();
  const { getResponseActionHandler } = useResponseActionsContext();
  const chainTokenContext = useFormTokenChainContextOptional();
  const { datasetControl } = useDatasetContext();
  // Dedicated instance for "fetch all rows" on submit with select-all.
  // Keeps the Grid's datasetData untouched so its useEffect doesn't react to
  // an unpaginated result right before the modal closes.
  const {
    fetch: fullFetch,
    detailedFetch: fullDetailedFetch,
    isLoading: fullFetchLoading,
  } = useDatasetData({ rethrowErrors: true });
  const {
    data: datasetData,
    fetch: fetchData,
    detailedFetch: detailedFetchData,
    error: dataError,
    isLoading: dataLoading,
  } = useDatasetData();

  const {
    data: datasetFilter,
    fetch: fetchFilter,
    error: filterError,
    isLoading: filterLoading,
  } = useFilter();

  const gridRef = useRef<HTMLDivElement | any>(null);
  const dataRef = useRef<any[]>(data ?? []);
  const offsetRef = useRef(offset);
  const lazyLoadingRef = useRef(lazyLoading);
  const cachedQueryIdRef = useRef(cachedQueryId);
  const disabledLazyLoadingRef = useRef(disabledLazyLoading);
  const activeSortingRef = useRef(activeSorting);
  // True whenever the user clicks the header select-all checkbox (on OR off).
  const selectAllToggledRef = useRef<boolean>(false);
  const { t } = useTranslate();

  const {
    activeFilters,
    activeFiltersRef,
    defaultFilter,
    lockedFilterParams,
    rawMergedFilters,
    getRawMergedFilters,
    getEffectiveFilterObject,
    resetActiveFilters,
    applyLazyStateFilters,
  } = useReferenceFilters(control);

  const rawDataset: Dataset | undefined = referenceDatasets[datasetId];
  const dataset = useDatasetVisibilityResolve(rawDataset, rawMergedFilters);
  const dataKey = getDataKey(dataset);
  // Exclusions end up in the submitted id string, so they must be keyed by the
  // column that gets submitted — not the grid's dataKey, which may be a
  // positional synthetic _id when the dataset has no idItem.
  const exclusionIdKey = control.idItem || dataKey;

  // The inserted row's control.idItem value, read from the loaded row (the
  // locate flow loads its chunk if it's not on the first page).
  // autoSelectInsertedId is the primary key, which may differ from
  // control.idItem; only the resolved value matches/commits.
  const [autoSelectResolvedId, setAutoSelectResolvedId] = useState<
    string | undefined
  >(undefined);

  // Folds the inserted row's control.idItem into the pre-selection value so it
  // gets selected as its page loads (and is preserved on confirm).
  const effectiveValue = useMemo(
    () =>
      buildReferenceValueWithInsertedId(
        value,
        autoSelectResolvedId,
        control.multiSelect,
        valueDelimiter
      ),
    [value, autoSelectResolvedId, control.multiSelect, valueDelimiter]
  );

  useSelectedRowsBatchUpdate(
    cachedQueryIdRef.current,
    data,
    datasetData,
    selectAllChecked,
    deselectedRowIds,
    exclusionIdKey,
    selectedVirtualRows,
    setSelectedRows,
    setSelectedVirtualRows
  );

  useSelectedRowsChangedCachedQueryGuard(
    cachedQueryIdRef.current,
    datasetData,
    control.datasetId,
    resetReferenceSelectedRows
  );

  const requestData = useMemo(() => {
    if (!dataset) return undefined;

    return getDatasetDataApiRequestData(
      dataset,
      activeSorting,
      sessionToken,
      0,
      rawMergedFilters ?? undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      datasetControl ?? undefined
    );
  }, [dataset, activeSorting, rawMergedFilters, sessionToken]);

  // customLimit (locate flow): fetch a larger first page so a row deeper in the
  // list is loaded; offset is advanced past it so later lazy loads don't overlap.
  const fetchReferenceData = async (
    lazy = false,
    queryId?: string,
    customLimit?: number
  ) => {
    if (!dataset || !rawDataset) {
      return;
    }

    if (!lazy && !queryId?.trim()) {
      setCachedQueryId('');
      cachedQueryIdRef.current = '';
    }

    const limit = customLimit ?? GRID_DATA_LIMIT;
    const dataOffset = lazy ? offsetRef.current + GRID_DATA_LIMIT : 0;
    setOffset(
      lazy ? dataOffset : customLimit ? customLimit - GRID_DATA_LIMIT : 0
    );

    if (!lazy) {
      setDisabledLazyLoading(false);
      // Scroll to the top of the list
      scrollGridToTop(gridRef);
    }

    const filters = getEffectiveFilterObject();

    const actualToken = chainTokenContext?.getActualTokenFromUrl();
    const createSubsetFormRequestData =
      String(dataset?.datasetId).includes('_') && isFormInput && actualToken;

    const requestDataset = await resolveDatasetItemsVisibility(
      rawDataset,
      getRawMergedFilters()
    );
    const items = getReferenceItemsParamArray(requestDataset, control.idItem);

    const requestData = createSubsetFormRequestData
      ? getSubsetDataApiRequestData(
          actualToken.meta?.requestData?.['master'].id ?? '',
          requestDataset,
          activeSortingRef.current || [],
          sessionToken,
          dataOffset,
          filters,
          limit,
          // A fresh request omits cachedQueryId; lazy requests reuse it.
          queryId,
          items,
          actualToken.meta?.requestData?.['master'].idItem ?? ''
        )
      : getDatasetDataApiRequestData(
          requestDataset,
          activeSortingRef.current || [],
          sessionToken,
          dataOffset,
          filters,
          limit,
          // A fresh request omits cachedQueryId; lazy requests reuse it.
          queryId,
          items
        );

    // String apiMethod flow — build URL params and fetch directly
    if (typeof dataset.apiMethod === 'string') {
      const { apiUrl } = await resolveTemplate(
        { apiUrl: dataset.apiMethod },
        requestData
      );

      const paramsString = getReferenceDataParamsString(
        activeFiltersRef.current,
        activeSortingRef.current,
        dataset,
        limit,
        dataOffset,
        items,
        defaultFilter
      );

      fetchData(`${apiUrl}${paramsString}`, dataset, lazy, queryId);
      return;
    }

    // DetailedApiMethod flow — build request data and resolve templates
    await apiRequest(
      dataset.apiMethod,
      requestData,
      (apiMethod) =>
        getDataFromDatasetRequest(() =>
          detailedFetchData(apiMethod, dataset, lazy)
        ),
      getResponseActionHandler
    );
  };

  const loadMoreData = () => {
    if (!lazyLoadingRef.current && !disabledLazyLoadingRef.current) {
      setLazyLoading(true);
      lazyLoadingRef.current = true;
      fetchReferenceData(true, cachedQueryIdRef.current);
    }
  };

  const submitFilter = async () => {
    if (!dataset) {
      return;
    }
    // Fast path: no virtual rows outstanding — submit selectedRows.
    // If the user hasn't toggled select-all in this session AND `value` contains
    // IDs not present in loaded `data`, include those too: they were implicitly
    // still selected on reopen and never displayed for the user to uncheck.
    if (selectedVirtualRows === 0) {
      const idSet = buildSelectedIdSet(
        selectedRows,
        control.idItem,
        effectiveValue,
        data,
        selectAllToggledRef.current,
        valueDelimiter,
        control.multiSelect,
        deselectedRowIds
      );
      handleOnSubmit(Array.from(idSet).join(valueDelimiter));
      return;
    }

    // Virtual rows present: fetch all (no offset/limit) to resolve unfetched
    // IDs before submitting. Mirrors useControlAction.fetchNeededDatasetData.
    setIsSubmitting(true);
    try {
      const filters = getEffectiveFilterObject();
      // No Query Cache for the all-rows fetch — null drops the header.
      const requestData = getDatasetDataApiRequestData(
        dataset,
        activeSortingRef.current || [],
        sessionToken,
        0,
        filters,
        undefined,
        null
      );
      // Strip paging params — API returns all rows without offset/limit.
      // Only request the idItem column — we only map row[idItem] at the end.
      delete requestData.limit;
      delete requestData.offset;
      requestData.items = [control.idItem];

      let result;
      // String apiMethod flow — build URL params and fetch directly
      if (typeof dataset.apiMethod === 'string') {
        const { apiUrl } = await resolveTemplate(
          { apiUrl: dataset.apiMethod },
          requestData
        );
        // No limit/offset; itemsOverride narrows to idItem only
        const paramsString = getReferenceDataParamsString(
          activeFiltersRef.current,
          activeSortingRef.current,
          dataset,
          undefined,
          undefined,
          [control.idItem],
          defaultFilter
        );
        result = await fullFetch(
          `${apiUrl}${paramsString}`,
          dataset,
          false,
          undefined
        );
      } else {
        // DetailedApiMethod flow — resolve templates and fetch
        result = await apiRequest(
          dataset.apiMethod,
          requestData,
          (apiMethod) => fullDetailedFetch(apiMethod, dataset),
          getResponseActionHandler
        );
      }

      // The fetch layer swallows api errors into undefined, so the catch below
      // never fires — bail here rather than submit a partial ID list.
      if (!result) {
        toast(t('errors.referenceLoadingError'));
        return;
      }

      const allRows = result?.data ?? [];
      const allIds: string[] = allRows.map((row: any) =>
        row[control.idItem]?.toString()
      );
      const finalIds = buildFinalIds(
        allIds,
        selectAllChecked,
        selectedRows,
        data,
        control.idItem,
        deselectedRowIds
      );
      handleOnSubmit(finalIds.join(valueDelimiter));
    } catch (error) {
      // Keep modal open so user can retry. Never submit a partial ID list.
      toast(
        isContentTooLargeError(error)
          ? t('toasts.contentTooLargeError')
          : t('errors.referenceLoadingError')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const setExpandedRow = (row: any) => {
    if (control.multiSelect) {
      setSelectedRows((oldRows) => {
        if (oldRows.some((v) => v[dataKey] === row[dataKey])) {
          return oldRows.filter((v) => v[dataKey] !== row[dataKey]);
        }
        return [...oldRows, row];
      });
    }
  };

  // Submit the double-clicked row directly — the two prior single clicks flip selectedRows, so it can't be trusted.
  const handleRowDoubleClick = (e: DataTableRowClickEvent): void => {
    const row = e.data as Record<string, unknown>;
    if (!row || isSkeletonRow(row[dataKey] as string)) {
      return;
    }
    const idSet = buildSelectedIdSet(
      [row],
      control.idItem,
      effectiveValue,
      data,
      selectAllToggledRef.current,
      valueDelimiter,
      control.multiSelect,
      deselectedRowIds
    );
    handleOnSubmit(Array.from(idSet).join(valueDelimiter));
  };

  useEffect(() => {
    disabledLazyLoadingRef.current = disabledLazyLoading;
  }, [disabledLazyLoading]);

  useEffect(() => {
    lazyLoadingRef.current = lazyLoading;
  }, [lazyLoading]);

  useEffect(() => {
    // Keep lazy-load pagination in sync so the next request advances offset correctly.
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    activeSortingRef.current = activeSorting;
  }, [activeSorting]);

  useEffect(() => {
    cachedQueryIdRef.current = cachedQueryId;
  }, [cachedQueryId]);

  // Update data ref when data changes due to bug in primereact on checkbox click handler
  // => not updating reference on function
  useEffect(() => {
    if (data) {
      dataRef.current = data;
    }
  }, [data]);

  useAutoSelectInsertedRow({
    autoSelectInsertedId,
    data,
    dataset,
    dataKey,
    control,
    sessionToken,
    gridRef,
    activeFiltersRef,
    defaultFilter,
    activeSortingRef,
    cachedQueryIdRef,
    disabledLazyLoadingRef,
    setAutoSelectResolvedId,
    setSelectedRows,
    setCachedQueryId,
    setDisabledLazyLoading,
    fetchReferenceData,
  });

  useEffect(() => {
    if (!dataset) {
      fetchDataset();
    }
    // Reset on mount so prior opens of the modal don't leak selection state.
    resetReferenceSelectedRows();
    selectAllToggledRef.current = false;
  }, []);

  useEffect(() => {
    if (
      fetchedDataset?.apiMethod &&
      fetchedDataset?.datasetId === control.datasetId
    ) {
      setReferenceDatasets((v) => ({
        ...v,
        [control.datasetId]: fetchedDataset,
      }));
    }
  }, [fetchedDataset]);

  useEffect(() => {
    if (dataset) {
      const multiSortMeta = getMultisortFromDataset(dataset);
      const newFilters = getDefaultLazyStateFilters(dataset);
      // The locked default filter is NOT folded in here — initialise the user
      // layer as empty (non-null) so the first-fetch gate below fires.
      resetActiveFilters();
      const sorting = multiSortMeta || [];
      activeSortingRef.current = sorting;
      setActiveSorting(sorting);
      setLazyState((oldLazyState) => ({
        ...oldLazyState,
        multiSortMeta: multiSortMeta || [],
        filters: newFilters,
      }));
      setDatasetLoading(false);
    }
  }, [dataset]);

  useEffect(() => {
    // Prevents bugs with data requested before page change
    if (datasetData && datasetData.datasetId === control?.datasetId) {
      if (!dataset) return;
      let newData: any[] = [];
      if (datasetData.data.length < GRID_DATA_LIMIT) {
        setDisabledLazyLoading(true);
      }
      const isLazyAppend =
        datasetData.lazy &&
        (datasetData.cachedQueryId === cachedQueryIdRef.current ||
          // Use lazyloading withoud server cache id if server did not return X-CACHEDQUERY-ID
          datasetData.cachedQueryId === undefined);
      // add unique row id to datasets that have no unique id
      newData = dataset?.idItem
        ? datasetData.data
        : withSyntheticRowIds(
            datasetData.data,
            isLazyAppend ? (dataRef.current?.length ?? 0) : 0
          );
      const newColumnsWidth = getColumnsWidth(
        dataset,
        newData,
        columnsWidth,
        lazyState
      );
      setColumnsWidth(newColumnsWidth);
      if (datasetData.lazy) {
        setLazyLoading(false);
        if (isLazyAppend) {
          setData((oldData) => {
            if (!oldData) {
              return newData;
            }
            return [...oldData, ...newData];
          });
          // Pre-select lazy-loaded rows whose IDs are in the effective value.
          // Skip when selectAllChecked — defensive useEffect handles that path.
          if (effectiveValue !== '' && !selectAllChecked) {
            // Single-select stores exactly one id; splitting would break ids that contain the delimiter (e.g. names with commas).
            const splittedValue = control.multiSelect
              ? effectiveValue.split(valueDelimiter).filter(Boolean)
              : [effectiveValue];
            const matching = newData.filter((row) =>
              splittedValue.includes(row[control.idItem].toString())
            );
            if (matching.length > 0) {
              setSelectedRows((prev) => {
                // Dedup by dataKey in case a row is already selected.
                const existingKeys = new Set(prev.map((r) => r[dataKey]));
                const toAdd = matching.filter(
                  (r) => !existingKeys.has(r[dataKey])
                );
                if (toAdd.length === 0) return prev;
                return [...prev, ...toAdd];
              });
            }
          }
        } else {
          // Reset data if X-CACHEDQUERY-ID changed
          toast(t('toasts.newDataLoaded'));
          setOffset(0);
          // Scroll to the top of table
          scrollGridToTop(gridRef);
          setData(newData);
        }
      } else {
        setData(newData);
        // Explicit unchecks outrank the value-derived restore, which would
        // otherwise re-select them on every sort/filter refetch.
        if (effectiveValue !== '' && deselectedRowIds.size === 0) {
          // Single-select stores exactly one id; splitting would break ids that contain the delimiter (e.g. names with commas).
          const splittedValue = control.multiSelect
            ? effectiveValue.split(valueDelimiter).filter(Boolean)
            : [effectiveValue];
          const newSelectedRows = newData.filter((row) =>
            splittedValue.includes(row[control.idItem].toString())
          );
          setSelectedRows(newSelectedRows);
          // Restore select-all + virtual rows when IDs cover the full dataset
          // (mirrors processSelectAllReferenceChange). Falls back to newData.length
          // when X-CACHEDQUERY-TOTALCNT header is absent (small unpaginated sets).
          const totalCount = datasetData?.totalCount ?? newData.length;
          if (splittedValue.length >= totalCount && totalCount > 0) {
            setSelectAllChecked(true);
            setSelectedVirtualRows(totalCount - newSelectedRows.length);
          }
        }
      }
      if (datasetData.cachedQueryId !== undefined) {
        setCachedQueryId(datasetData.cachedQueryId);
        // Sync ref synchronously so an immediate follow-up lazy load (e.g. triggered
        // by scrolling to a located row) reads the fresh cachedQueryId, not the stale
        // value — otherwise the server returns a new query id and triggers the
        // newDataLoaded toast.
        cachedQueryIdRef.current = datasetData.cachedQueryId;
      }
      if (dataset?.filterId && !filter && !filterLoading) {
        fetchFilter(dataset.filterId, dataset.datasetId);
      }
      setGridLoading(false);
    }
  }, [datasetData]);

  useEffect(() => {
    if (dataset && !datasetLoading) {
      setActiveSorting((oldActiveSorting) =>
        updateActiveSortingFromLazyState(oldActiveSorting, lazyState)
      );
      if (lazyState.filters !== null) {
        applyLazyStateFilters(lazyState, dataset);
      }
    }
  }, [lazyState]);

  useEffect(() => {
    if (
      !datasetLoading &&
      dataset?.apiMethod &&
      dataset?.datasetId === control?.datasetId &&
      activeFilters !== null &&
      activeSorting !== null
    ) {
      fetchReferenceData(false, undefined);
    }
  }, [datasetLoading, activeFilters, activeSorting]);

  useEffect(() => {
    if (datasetFilter && datasetFilter.datasetId === control?.datasetId) {
      if (dataset?.items && datasetFilter.filter.items) {
        // Update default matchMode value for multiCheckbox and reference filters
        setLazyState((oldLazyState) => {
          const newFilters = { ...oldLazyState.filters };
          dataset.items?.forEach((column) => {
            // control.filter no longer seeds lazyState constraints (it's a
            // hidden locked filter), so its columns take defaults like any other.
            if (column.filterParam && newFilters[column.name]) {
              const filterItem = datasetFilter.filter.items.find(
                ({ name }) => name === column.filterParam
              );
              // Keep the existing matchMode once a value is set; rewriting it would pair that value with a foreign matchMode.
              if (
                filterItem &&
                isEmptyValue(newFilters[column.name].constraints[0].value)
              ) {
                newFilters[column.name].constraints[0].matchMode =
                  getSupportedDefaultMatchMode(filterItem, column.type);
              }
            }
          });
          return { ...oldLazyState, filters: newFilters };
        });
      }
      setFilter(datasetFilter.filter);
    }
  }, [datasetFilter]);

  if (
    (datasetError && datasetError.datasetId === control?.datasetId) ||
    (dataError && dataError.datasetId === control?.datasetId) ||
    (filterError && filterError.datasetId === control?.datasetId)
  ) {
    const error = datasetError || dataError || filterError;
    return <ApiError error={error?.error} />;
  }

  const isLoading =
    (gridLoading || dataLoading || filterLoading || datasetLoading) &&
    !lazyLoading;

  // No FunctionalButtonsRow in this header, so the locked branch never applies.
  const datasetWrapperStyle = getDatasetButtonsWrapperInlineStyle(
    datasetMinWidth,
    false
  );

  const referenceButtons = (
    <>
      <Button
        onClick={submitFilter}
        disabled={isSubmitting}
        className="fullscreen-reference-header-btn"
        fill="clear"
        color="success"
      >
        <IonIcon icon={checkmarkCircleOutline} slot="start" />
        {t('confirmSelection')}
      </Button>
      <Button
        onClick={closeFullscreenReference}
        className="fullscreen-reference-header-btn"
        fill="clear"
        color="danger"
      >
        <IonIcon icon={closeCircleOutline} slot="start" />
        {t('discardChanges')}
      </Button>
    </>
  );

  const handleDatasetButtonsOverflowChange = (
    state: DatasetButtonsOverflowState
  ): void => {
    setDatasetMinWidth((previous) =>
      previous === state.collapsedWidth ? previous : state.collapsedWidth
    );
  };

  const reloadButton = (
    <Button
      onClick={() => fetchReferenceData(false)}
      className="header-btn fullscreen-reference-reload-btn"
      fill="clear"
      round={false}
      disabled={isLoading}
    >
      <IonIcon icon={refreshOutline} slot="start" />
      {t('reload')}
    </Button>
  );

  if (!dataset) {
    return (
      <div className="fullscreen-reference-loading">
        <SeptimLoadingSpinner />
      </div>
    );
  }

  return (
    <RequestDataProvider requestData={requestData}>
      {/* Overlay while the unpaginated "fetch all rows" request is in flight */}
      {fullFetchLoading && <Loading />}
      <div className="header">
        <HeaderLabel label={label} />
        {control.datasetControls ? (
          <>
            <div
              className="fullscreen-reference-dataset-buttons"
              style={datasetWrapperStyle}
            >
              <DatasetButtons
                activeFilters={activeFilters}
                activeSorting={activeSorting}
                data={data}
                dataset={dataset}
                datasetData={datasetData}
                currentRow={null}
                refetchData={fetchReferenceData}
                referenceDatasetControls={control.datasetControls}
                selectedRows={selectedRows}
                onOverflowChange={handleDatasetButtonsOverflowChange}
              >
                {referenceButtons}
              </DatasetButtons>
            </div>
            {reloadButton}
          </>
        ) : (
          <>
            {referenceButtons}
            {reloadButton}
          </>
        )}
      </div>
      <Grid
        dataset={dataset}
        filter={filter}
        data={data}
        dataLoading={isLoading}
        selectedRows={selectedRows}
        processSelectedRowsChange={(checkboxEvent) =>
          // dataRef.current — `data` state can lag (stale closure or
          // cached-queryId reset path), so use the ref like the master grid.
          processSelectedReferenceRowsChange(
            checkboxEvent,
            dataRef.current ?? [],
            datasetData,
            dataKey,
            selectedVirtualRows,
            exclusionIdKey
          )
        }
        processSelectAllChange={(checkboxEvent) => {
          // Mark session as having explicit select-all interaction —
          // fast-path's "preserve value's virtual IDs" is then disabled.
          selectAllToggledRef.current = true;
          processSelectAllReferenceChange(
            checkboxEvent,
            dataRef.current ?? [],
            datasetData
          );
        }}
        resetSelectedRows={resetReferenceSelectedRows}
        selectAllChecked={selectAllChecked}
        setExpandedRow={setExpandedRow}
        onRowDoubleClick={
          control?.multiSelect ? undefined : handleRowDoubleClick
        }
        lazyState={lazyState}
        setLazyState={setLazyState}
        loadMoreRows={loadMoreData}
        dataKey={dataKey}
        gridRef={gridRef}
        disabledLazyLoading={disabledLazyLoading}
        columnsWidth={columnsWidth}
        referenceDatasets={referenceDatasets}
        setReferenceDatasets={setReferenceDatasets}
        disableMultiselect={!control?.multiSelect}
        isReferenceGrid
        lockedFilterParams={lockedFilterParams}
        gridHeaderHeight={HEADER_MIN_HEIGHT_PX + breadcrumbsHeight}
      />
    </RequestDataProvider>
  );
};

export default FullscreenReference;
